import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Switch } from '../../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../ui/alert-dialog';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  Zap, 
  Plus, 
  Edit, 
  Trash2, 
  Settings,
  Target,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Save,
  Brain,
  Clock
} from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';

const AutoResolution = () => {
  const { currentBusiness } = useBusiness();
  const { t } = useLanguage();
  const { toast } = useToast();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [aiConfigOpen, setAiConfigOpen] = useState(false);
  
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    keywords: '',
    response: '',
    confidence_threshold: 0.8,
    enabled: true
  });

  const [editRule, setEditRule] = useState({
    id: '',
    name: '',
    description: '',
    keywords: '',
    response: '',
    confidence_threshold: 0.8,
    enabled: true
  });

  const [aiConfig, setAiConfig] = useState({
    // Real current model (backend /ai-agent/config returns the live provider/model). If the provider
    // is ever switched (e.g. to Claude as a future fallback), these labels reflect it automatically.
    model: 'gpt-4o-mini',
    provider: 'openai',
    confidenceThreshold: 0.85,
    maxResponseTime: 3,
    autoEscalation: true,
    learningMode: 'active'
  });

  // D4 — canonical Firebase-authenticated request (fresh ID token + backend base + 401 refresh).
  const { makeAuthenticatedRequest } = useCRMAuth();

  // Fetch rules from backend
  useEffect(() => {
    const fetchRules = async () => {
      if (!currentBusiness?.brandId) return;
      
      setLoading(true);
      try {
        const response = await makeAuthenticatedRequest(`/api/v1/ai-agent/auto-resolution-rules/${currentBusiness.brandId}`);
        if (response.ok) {
          const data = await response.json();
          setRules(data.rules || []);
        } else {
          // D4 — no mock fallback: surface a truthful failure instead of fabricating rules.
          throw new Error(`Failed to load rules (HTTP ${response.status})`);
        }
      } catch (error) {
        console.error('Error fetching auto-resolution rules:', error);
        toast({
          title: t('common.error'),
          description: t('aa.loadRulesError'),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, [currentBusiness?.brandId]);

  const handleSaveRule = async () => {
    if (!newRule.name || !newRule.response) {
      toast({
        title: t('aa.missingInfo'),
        description: t('aa.fillRuleNameResponse'),
        variant: "destructive",
      });
      return;
    }

    const rule = {
      ...newRule,
      id: `rule-${Date.now()}`,
      business_id: currentBusiness?.brandId || 'default',
      usage_count: 0,
      success_rate: 0,
      keywords: newRule.keywords.split(',').map(k => k.trim()).join(', ')
    };

    try {
      const response = await makeAuthenticatedRequest(`/api/v1/ai-agent/auto-resolution-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rule)
      });

      if (response.ok) {
        setRules(prev => [...prev, rule]);
        setNewRule({
          name: '',
          description: '',
          keywords: '',
          response: '',
          confidence_threshold: 0.8,
          enabled: true
        });

        toast({
          title: t('aa.ruleCreated'),
          description: t('aa.ruleCreatedDesc'),
        });
      } else {
        throw new Error('Failed to create rule');
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('aa.createRuleError'),
        variant: "destructive",
      });
    }
  };

  const handleEditRule = async () => {
    if (!editRule.name || !editRule.response) {
      toast({
        title: t('aa.missingInfo'),
        description: t('aa.fillRuleNameResponse'),
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`/api/v1/ai-agent/auto-resolution-rules/${editRule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editRule,
          keywords: editRule.keywords.split(',').map(k => k.trim()).join(', ')
        })
      });

      if (response.ok) {
        setRules(prev => prev.map(rule => 
          rule.id === editRule.id 
            ? { ...editRule, keywords: editRule.keywords.split(',').map(k => k.trim()).join(', ') }
            : rule
        ));
        
        setEditDialogOpen(false);
        setSelectedRule(null);
        
        toast({
          title: t('aa.ruleUpdated'),
          description: t('aa.ruleUpdatedDesc'),
        });
      } else {
        throw new Error('Failed to update rule');
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('aa.updateRuleError'),
        variant: "destructive",
      });
    }
  };

  const openEditDialog = (rule) => {
    setEditRule({
      id: rule.id,
      name: rule.name,
      description: rule.description,
      keywords: rule.keywords,
      response: rule.response,
      confidence_threshold: rule.confidence_threshold,
      enabled: rule.enabled
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (rule) => {
    setSelectedRule(rule);
    setDeleteDialogOpen(true);
  };

  const toggleRule = async (ruleId) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;

    try {
      const response = await makeAuthenticatedRequest(`/api/v1/ai-agent/auto-resolution-rules/${ruleId}/toggle`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        setRules(prev => prev.map(rule => 
          rule.id === ruleId 
            ? { ...rule, enabled: !rule.enabled }
            : rule
        ));
        
        toast({
          title: t('aa.ruleUpdated'),
          description: t(rule.enabled ? 'aa.ruleDisabledSuccess' : 'aa.ruleEnabledSuccess'),
        });
      }
    } catch (error) {
      console.error('Error toggling rule:', error);
    }
  };

  const deleteRule = async () => {
    if (!selectedRule) return;

    try {
      const response = await makeAuthenticatedRequest(`/api/v1/ai-agent/auto-resolution-rules/${selectedRule.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setRules(prev => prev.filter(rule => rule.id !== selectedRule.id));
        setDeleteDialogOpen(false);
        setSelectedRule(null);
        
        toast({
          title: t('aa.ruleDeleted'),
          description: t('aa.ruleDeletedDesc'),
        });
      } else {
        throw new Error('Failed to delete rule');
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('aa.deleteRuleError'),
        variant: "destructive",
      });
    }
  };

  const handleAIConfigSave = async () => {
    try {
      const response = await makeAuthenticatedRequest(`/api/v1/ai-agent/config/${currentBusiness?.brandId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(aiConfig)
      });

      if (response.ok) {
        setAiConfigOpen(false);
        toast({
          title: t('aa.aiConfigUpdated'),
          description: t('aa.aiConfigSaved'),
        });
      } else {
        throw new Error('Failed to update AI configuration');
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('aa.updateConfigError'),
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex-1 h-full flex items-center justify-center bg-gradient-to-br from-white via-blue-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('aa.loadingRules')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-blue-50 to-purple-50">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('aa.autoResolutionMgmt')}</h1>
            <p className="text-gray-600">{t('aa.autoResolutionSubtitle')}</p>
          </div>
          <div className="flex items-center space-x-3">
            <Badge className="bg-blue-100 text-blue-800">
              {t('aa.activeRulesBadge', { count: rules.filter(r => r.enabled).length })}
            </Badge>

            <Dialog open={aiConfigOpen} onOpenChange={setAiConfigOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                  <Settings className="h-4 w-4 mr-2" />
                  {t('aa.aiSettings')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{t('aa.configTitle')}</DialogTitle>
                  <DialogDescription>
                    {t('aa.configDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">{t('aa.aiModel')}</label>
                    <div className="p-3 bg-blue-50 rounded-lg border">
                      <div className="flex items-center space-x-2">
                        <Brain className="h-5 w-5 text-purple-600" />
                        <span className="font-medium">{aiConfig.provider ? `${aiConfig.provider} · ${aiConfig.model}` : aiConfig.model}</span>
                        <Badge className="bg-purple-100 text-purple-800">{t('aa.active')}</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{t('aa.advancedReasoning')}</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium">{t('aa.confidenceThreshold')}</label>
                    <Input
                      type="number"
                      min="0.1"
                      max="1.0"
                      step="0.05"
                      value={aiConfig.confidenceThreshold}
                      onChange={(e) => setAiConfig(prev => ({ ...prev, confidenceThreshold: parseFloat(e.target.value) }))}
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('aa.confidenceThresholdHint')}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium">{t('aa.maxResponseTime')}</label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={aiConfig.maxResponseTime}
                      onChange={(e) => setAiConfig(prev => ({ ...prev, maxResponseTime: parseInt(e.target.value) }))}
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <label className="text-sm font-medium">{t('aa.advancedFeatures')}</label>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={aiConfig.autoEscalation}
                          onChange={(e) => setAiConfig(prev => ({ ...prev, autoEscalation: e.target.checked }))}
                          className="rounded"
                        />
                        <span className="text-sm">{t('aa.autoEscalation')}</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={aiConfig.learningMode === 'active'}
                          onChange={(e) => setAiConfig(prev => ({ ...prev, learningMode: e.target.checked ? 'active' : 'passive' }))}
                          className="rounded"
                        />
                        <span className="text-sm">{t('aa.activeLearningFromInteractions')}</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setAiConfigOpen(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={handleAIConfigSave}>
                      {t('aa.saveConfiguration')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Performance Overview */}
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('aa.activeRules')}</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {rules.filter(r => r.enabled).length}
                  </p>
                </div>
                <Zap className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('aa.avgSuccessRate')}</p>
                  <p className="text-2xl font-bold text-green-600">
                    {rules.length > 0 ? (rules.reduce((sum, rule) => sum + rule.success_rate, 0) / rules.length).toFixed(1) : 0}%
                  </p>
                </div>
                <Target className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('aa.totalUsage')}</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {rules.reduce((sum, rule) => sum + rule.usage_count, 0)}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('aa.aiModel')}</p>
                  <p className="text-sm font-bold text-orange-600">{aiConfig.model}</p>
                  <p className="text-xs text-gray-500">{t('aa.advancedAi')}</p>
                </div>
                <Brain className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs defaultValue="rules" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="rules">{t('aa.tab.rules')}</TabsTrigger>
          <TabsTrigger value="create">{t('aa.tab.create')}</TabsTrigger>
          <TabsTrigger value="settings">{t('aa.aiSettings')}</TabsTrigger>
        </TabsList>

        {/* Resolution Rules Tab */}
        <TabsContent value="rules" className="space-y-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle>{t('aa.autoResolutionRulesCount', { count: rules.length })}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {rules.map((rule) => (
                  <div key={rule.id} className="p-4 border rounded-lg bg-white/50">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-medium text-gray-900">{rule.name}</h3>
                          <Switch 
                            checked={rule.enabled}
                            onCheckedChange={() => toggleRule(rule.id)}
                          />
                          <Badge className={rule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                            {rule.enabled ? <><CheckCircle className="h-3 w-3 inline mr-1" />{t('aa.active')}</> : t('aa.disabled')}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{rule.description}</p>
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span className="flex items-center"><Clock className="h-3 w-3 mr-1" />{t('aa.keywordsLabel', { keywords: rule.keywords })}</span>
                          <span>{t('aa.confidenceLabel', { value: rule.confidence_threshold * 100 })}</span>
                          <span>{t('aa.usedLabel', { count: rule.usage_count })}</span>
                          <span className="flex items-center">
                            {rule.success_rate >= 90
                              ? <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                              : <AlertTriangle className="h-3 w-3 mr-1 text-yellow-500" />}
                            {t('aa.successLabel', { value: rule.success_rate })}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline" onClick={() => openEditDialog(rule)}>
                          <Edit className="h-3 w-3" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-red-600"
                              onClick={() => openDeleteDialog(rule)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </AlertDialogTrigger>
                        </AlertDialog>
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded text-sm">
                      <strong>{t('aa.responseLabel')}</strong> {rule.response}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Create Rule Tab */}
        <TabsContent value="create" className="space-y-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Plus className="h-5 w-5 text-green-600" />
                <span>{t('aa.createNewRule')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('aa.ruleName')}</label>
                  <Input
                    placeholder={t('aa.ruleNamePlaceholder')}
                    value={newRule.name}
                    onChange={(e) => setNewRule(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('aa.confidenceThreshold')}</label>
                  <Input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={newRule.confidence_threshold}
                    onChange={(e) => setNewRule(prev => ({ ...prev, confidence_threshold: parseFloat(e.target.value) }))}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">{t('aa.description')}</label>
                <Input
                  placeholder={t('aa.descriptionPlaceholder')}
                  value={newRule.description}
                  onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>

              <div>
                <label className="text-sm font-medium">{t('aa.keywordsCommaSeparated')}</label>
                <Input
                  placeholder={t('aa.keywordsPlaceholder')}
                  value={newRule.keywords}
                  onChange={(e) => setNewRule(prev => ({ ...prev, keywords: e.target.value }))}
                />
              </div>

              <div>
                <label className="text-sm font-medium">{t('aa.autoResponse')}</label>
                <Textarea
                  placeholder={t('aa.autoResponsePlaceholder')}
                  value={newRule.response}
                  onChange={(e) => setNewRule(prev => ({ ...prev, response: e.target.value }))}
                  rows={4}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={newRule.enabled}
                  onCheckedChange={(checked) => setNewRule(prev => ({ ...prev, enabled: checked }))}
                />
                <label className="text-sm">{t('aa.enableRuleImmediately')}</label>
              </div>

              <Button
                onClick={handleSaveRule}
                className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {t('aa.saveRule')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle>{t('aa.aiConfigSettings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border">
                <div className="flex items-center space-x-3 mb-4">
                  <Brain className="h-8 w-8 text-purple-600" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{t('aa.aiEngine')}</h3>
                    <p className="text-sm text-gray-600">{t('aa.poweredBy', { provider: aiConfig.provider, model: aiConfig.model })}</p>
                  </div>
                </div>

                {/* Resolution accuracy / avg response time were hardcoded fakes. This tab does not
                    fetch AI metrics, so show honest placeholders (real figures live on the AI
                    Analytics dashboard). real wiring lands in the AI backend phase */}
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="p-3 bg-white rounded border">
                    <div className="text-lg font-semibold text-green-600">—</div>
                    <div className="text-sm text-gray-600">{t('aa.resolutionAccuracy')}</div>
                  </div>
                  <div className="p-3 bg-white rounded border">
                    <div className="text-lg font-semibold text-blue-600">—</div>
                    <div className="text-sm text-gray-600">{t('aa.avgResponseTime')}</div>
                  </div>
                </div>

                <Button
                  onClick={() => setAiConfigOpen(true)}
                  className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  {t('aa.configureAiParams')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Rule Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('aa.editRuleTitle')}</DialogTitle>
            <DialogDescription>
              {t('aa.editRuleDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('aa.ruleName')}</label>
                <Input
                  placeholder={t('aa.ruleNamePlaceholder')}
                  value={editRule.name}
                  onChange={(e) => setEditRule(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('aa.confidenceThreshold')}</label>
                <Input
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  value={editRule.confidence_threshold}
                  onChange={(e) => setEditRule(prev => ({ ...prev, confidence_threshold: parseFloat(e.target.value) }))}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">{t('aa.description')}</label>
              <Input
                placeholder={t('aa.descriptionPlaceholder')}
                value={editRule.description}
                onChange={(e) => setEditRule(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium">{t('aa.keywordsCommaSeparated')}</label>
              <Input
                placeholder={t('aa.keywordsPlaceholder')}
                value={editRule.keywords}
                onChange={(e) => setEditRule(prev => ({ ...prev, keywords: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium">{t('aa.autoResponse')}</label>
              <Textarea
                placeholder={t('aa.autoResponsePlaceholder')}
                value={editRule.response}
                onChange={(e) => setEditRule(prev => ({ ...prev, response: e.target.value }))}
                rows={4}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={editRule.enabled}
                onCheckedChange={(checked) => setEditRule(prev => ({ ...prev, enabled: checked }))}
              />
              <label className="text-sm">{t('aa.ruleIsEnabled')}</label>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleEditRule}>
                <Save className="h-4 w-4 mr-2" />
                {t('aa.updateRule')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('aa.areYouSure')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('aa.deleteConfirmPre')}
              <strong> "{selectedRule?.name}"</strong> {t('aa.deleteConfirmPost')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteRule}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('aa.deleteRule')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AutoResolution;